import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Alert,
  Button,
  Chip,
  TextField,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useSubscriptionPricing } from 'context/SubscriptionPricingContext';
import { useDuration } from 'context/DurationContext';
import { useCountry } from 'context/CountryContext';
import { useSubscriptionForm } from 'hooks/useSubscriptionForm';
import ModularEntitySelector from 'components/common/ModulerEntitySelector';
import { ENTITY_CONFIG } from 'config/entityConfig';
import {
  getEntityLabel,
  getContextDisplayNames,
  shouldShowSection,
} from 'utils/subscriptionRulesUtils';

const CustomPricingSection = ({
  subscriptionType,
  moduleType,
  duration,
  country,
  onEntityOverrideUpdate,
  isDiscountAllowed = true,
}) => {
  const [singleDiscount, setSingleDiscount] = useState(0);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const {
    setEntityOverride,
    removeEntityOverride,
    getEntityName,
    getEntityType,
    getAllOverrides,
    clearAllOverrides,
  } = useSubscriptionPricing();

  const { durations } = useDuration();
  const { countries } = useCountry();
  const { setValue, trigger, errors } = useSubscriptionForm();

  const entityLabel = getEntityLabel(moduleType, ENTITY_CONFIG);
  const context = getContextDisplayNames(
    duration,
    country,
    subscriptionType,
    durations,
    countries,
  );

  const showCustomPricing = shouldShowSection(
    subscriptionType,
    'showCustomPricing',
  );

  useEffect(() => {
    const overrides = getAllOverrides(subscriptionType);
    const entityIds = Object.keys(overrides);
    
    if (entityIds.length > 0) {
      setSelectedEntityIds(entityIds);
      
      const entities = entityIds.map(id => ({
        id,
        name: getEntityName(id) || id,
        type: getEntityType(id) || moduleType,
      }));
      
      setSelectedEntities(entities);
      setValue('customPricing.selectedEntityIds', entityIds);
      
      const firstOverride = overrides[entityIds[0]];
      if (firstOverride?.discountPercentage !== undefined) {
        setAppliedDiscount(firstOverride.discountPercentage);
        setSingleDiscount(firstOverride.discountPercentage);
      }
    }
  }, [subscriptionType, getAllOverrides, getEntityName, getEntityType, moduleType, setValue]);

  const handleEntityChange = (entityIds) => {
    const entities = entityIds.map(id => ({
      id,
      name: getEntityName(id) || id,
      type: getEntityType(id) || moduleType,
    }));

    setSelectedEntityIds(entityIds);
    setSelectedEntities(entities);
    setValue('customPricing.selectedEntityIds', entityIds);

    // For no-discount subscription types (e.g. cancellation-allowed) there is no separate
    // "Apply discount" step — selecting the items IS the configuration. Persist the standard-
    // pricing overrides immediately so the wizard's required-field validation (which reads
    // from saved overrides via getAllOverrides) passes without needing an extra "Apply" click.
    if (!isDiscountAllowed) {
      const previouslySelected = Object.keys(getAllOverrides(subscriptionType));
      entityIds.forEach(entityId => {
        setEntityOverride(entityId, subscriptionType, {
          discountPercentage: 0,
          moduleType,
          entityName: getEntityName(entityId),
          entityType: getEntityType(entityId) || moduleType,
          isCustomPricing: true,
          standardPricing: true,
        });
      });
      previouslySelected
        .filter(id => !entityIds.includes(id))
        .forEach(id => removeEntityOverride(id, subscriptionType));
      setIsDirty(false);
      onEntityOverrideUpdate?.();
    } else {
      setIsDirty(true);
    }

    trigger('customPricing.selectedEntityIds');
  };

  const handleSaveSelection = () => {
    if (selectedEntityIds.length === 0) {
      alert(`Please select at least one ${moduleType?.toLowerCase()} first`);
      return;
    }
    
    selectedEntityIds.forEach(entityId => {
      const entityName = getEntityName(entityId);
      const entityType = getEntityType(entityId);
      
      setEntityOverride(entityId, subscriptionType, {
        discountPercentage: 0,
        moduleType: moduleType,
        entityName: entityName,
        entityType: entityType || moduleType,
        isCustomPricing: true,
        standardPricing: true,
      });
    });

    setIsDirty(false);
    onEntityOverrideUpdate?.();
  };

  const handleRemoveEntity = (entityId) => {
    removeEntityOverride(entityId, subscriptionType);
    
    const updatedIds = selectedEntityIds.filter(id => id !== entityId);
    const updatedEntities = selectedEntities.filter(entity => entity.id !== entityId);
    
    setSelectedEntityIds(updatedIds);
    setSelectedEntities(updatedEntities);
    setValue('customPricing.selectedEntityIds', updatedIds);
    
    setIsDirty(true);
    onEntityOverrideUpdate?.();
  };

  const handleRemoveAllSelections = () => {
    clearAllOverrides(subscriptionType);
    
    setSelectedEntityIds([]);
    setSelectedEntities([]);
    setValue('customPricing.selectedEntityIds', []);
    setAppliedDiscount(null);
    setSingleDiscount(0);
    
    setIsDirty(false);
    onEntityOverrideUpdate?.();
  };

  const handleSingleDiscountChange = (value) => {
    const clamped = Math.min(Math.max(0, parseInt(value) || 0), 100);
    
    setSingleDiscount(clamped);
    setValue('customPricing.customDiscount', clamped);
    
    selectedEntityIds.forEach(entityId => {
      setValue(`customPricing.discounts.${entityId}`, clamped);
    });
    
    setIsDirty(true);
    trigger('customPricing.customDiscount');
  };

  const handleSaveDiscount = () => {
    if (selectedEntityIds.length === 0) {
      alert(`Please select at least one ${moduleType?.toLowerCase()} first`);
      return;
    }
    
    selectedEntityIds.forEach(entityId => {
      const entityName = getEntityName(entityId);
      const entityType = getEntityType(entityId);
      
      setEntityOverride(entityId, subscriptionType, {
        discountPercentage: singleDiscount,
        moduleType: moduleType,
        entityName: entityName,
        entityType: entityType || moduleType,
      });
    });

    const updatedEntities = selectedEntities.map(entity => ({
      ...entity,
      discount: singleDiscount
    }));
    setSelectedEntities(updatedEntities);
    
    setIsDirty(false);
    setAppliedDiscount(singleDiscount);
    
    onEntityOverrideUpdate?.();
  };

  const selectedCount = selectedEntityIds.length;
  const hasCustomSelections = selectedCount > 0;
  const hasUnsavedChanges = isDirty && selectedCount > 0;
  const isDiscountApplied = appliedDiscount !== null;

  if (!showCustomPricing) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Custom Item Selection for {moduleType}
          </Typography>
          
          {hasCustomSelections && (
            <Box display="flex" gap={1.5} alignItems="center">
              <Chip
                label={`${selectedCount} Selected`}
                size="small"
              />
              
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleRemoveAllSelections}
                startIcon={<DeleteIcon />}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {isDiscountAllowed 
            ? `Set custom discount for specific ${entityLabel} in ${context.countryDisplay}`
            : `Select specific ${entityLabel} for standard pricing in ${context.countryDisplay}`
          }
        </Alert>

        {errors.customPricing?.selectedEntityIds && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.customPricing.selectedEntityIds.message}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Entity Selector */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Step 1: Select {moduleType}
              </Typography>

              <ModularEntitySelector
                context="SUBSCRIPTION"
                entityType={moduleType}
                durationDisplay={context?.durationDisplay}
                countryDisplay={context?.countryDisplay}
                duration={duration}      
                country={country}
                selectedIds={selectedEntityIds}
                onChange={handleEntityChange}
                selectionType="multiple"
                required={false}
                helperText={`Select ${moduleType?.toLowerCase()}`}
                error={errors.customPricing?.selectedEntityIds?.message}
                maxSelections={50}
              />
            </Paper>
          </Grid>

          {/* Standard Pricing Section */}
          {!isDiscountAllowed && selectedCount > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Step 2: Confirm Standard Pricing
                </Typography>

                <Alert severity="info" sx={{ mb: 2.5 }}>
                  All selected {entityLabel} will receive standard pricing with no discounts.
                </Alert>

                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSelection}
                    disabled={!hasUnsavedChanges}
                    size="small"
                  >
                    {hasUnsavedChanges ? 'Apply' : 'Applied'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveAllSelections}
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>

                {hasUnsavedChanges && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Selection not saved yet.
                  </Alert>
                )}
              </Paper>
            </Grid>
          )}

          {isDiscountAllowed && selectedCount > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Step 2: Set Discount
                </Typography>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount Percentage"
                      type="number"
                      value={singleDiscount}
                      onChange={(e) => handleSingleDiscountChange(e.target.value)}
                      InputProps={{
                        endAdornment: <Typography sx={{ ml: 1 }}>%</Typography>,
                      }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      error={!!errors.customPricing?.customDiscount}
                      helperText={errors.customPricing?.customDiscount?.message}
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleSaveDiscount}
                      disabled={!hasUnsavedChanges && isDiscountApplied}
                      size="small"
                    >
                      {hasUnsavedChanges ? 'Apply' : 'Applied'}
                    </Button>
                  </Grid>
                </Grid>

                {isDiscountApplied && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {appliedDiscount}% discount applied to {selectedCount} {entityLabel.toLowerCase()}
                  </Alert>
                )}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Alert icon={<InfoIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                How This Works
              </Typography>
              <Box component="ol" sx={{ pl: 2, mb: 0, mt: 1, '& li': { mb: 0.5 } }}>
                <li>Select multiple {entityLabel} above</li>
                <li>{isDiscountAllowed ? 'Set discount percentage' : 'Standard pricing applies'}</li>
                <li>Click Apply to save</li>
                <li>Applies to {context.durationDisplay} in {context.countryDisplay}</li>
              </Box>
            </Alert>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomPricingSection;
